/**
 * Load native pixel PNGs (64×64 creatures / 96×64 dragons), knock out
 * exterior studio matte via edge flood-fill, then convert to vector-pixel
 * sheets (hard fillRect runs). Bake uses integer scale only — no soft
 * fractional resample. Interior whites/blacks (eyes, pupils) stay opaque.
 */

import {
  canvasToVectorSprite,
  vectorSpriteToCanvas,
  type VectorSprite,
} from "./vectorPixel";

export type CreatureKey =
  | "tideling"
  | "mosscurl"
  | "sparkwick"
  | "gloamink"
  | "reefbit";

export type DragonKey =
  | "seadragon-teal"
  | "seadragon-coral"
  | "seadragon-violet"
  | "seadragon-gold"
  | "seadragon-moss";

/** Body index 0–5 → PNG. Sixth slot reuses sparkwick (crest/spike silhouette). */
export const BODY_CREATURE_KEYS: CreatureKey[] = [
  "mosscurl", // ROUND — fluffy orb
  "reefbit", // HOP — tall ears
  "sparkwick", // HORN — crest / spikes
  "gloamink", // KIT — slender mink
  "tideling", // FINN — aquatic shellback
  "sparkwick", // SPIKE — crest reuse
];

/** Color index 0–4 → sea dragon PNG. Source art faces LEFT. */
export const DRAGON_SPRITE_KEYS: DragonKey[] = [
  "seadragon-teal",
  "seadragon-coral",
  "seadragon-violet",
  "seadragon-gold",
  "seadragon-moss",
];

/** All five source stills face LEFT in the art. */
export const CREATURE_SOURCE_FACES_LEFT: Record<CreatureKey, true> = {
  tideling: true,
  mosscurl: true,
  sparkwick: true,
  gloamink: true,
  reefbit: true,
};

const CREATURE_BASE = "/sprites/creatures";
const DRAGON_BASE = "/sprites/dragons";
/** Prefer modern formats (~80–110KB) over the ~1.5MB authored PNG. */
const SHORE_CANDIDATES = [
  "/sprites/scene/tropical-shore.avif",
  "/sprites/scene/tropical-shore.webp",
  "/sprites/scene/tropical-shore.png",
] as const;

/** Native creature sprite height (matches authored ~16px-tall PNGs). */
export const GAME_SPRITE_H = 64;
/** @deprecated use GAME_SPRITE_H */
export const GAME_SPRITE_H_SM = GAME_SPRITE_H;
/** @deprecated use GAME_SPRITE_H */
export const GAME_SPRITE_H_LG = GAME_SPRITE_H;

/** Native sea-dragon height (matches authored ~20px-tall PNGs). */
export const GAME_DRAGON_H = 64;

type Prepared = {
  vector: VectorSprite;
  canvas: HTMLCanvasElement;
  sm: HTMLCanvasElement;
  lg: HTMLCanvasElement;
};

const creatureCache = new Map<CreatureKey, Prepared>();
const dragonCache = new Map<DragonKey, Prepared>();
let shoreImage: HTMLImageElement | null = null;
let loadPromise: Promise<void> | null = null;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

async function loadShoreBackdrop(): Promise<HTMLImageElement> {
  let lastErr: unknown;
  for (const src of SHORE_CANDIDATES) {
    try {
      return await loadImage(src);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr ?? new Error("shore backdrop missing");
}

function luma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function isStudioMatte(r: number, g: number, b: number, a: number): boolean {
  if (a < 8) return true;
  const maxc = Math.max(r, g, b);
  const minc = Math.min(r, g, b);
  return maxc <= 40 && maxc - minc <= 22 && luma(r, g, b) <= 32;
}

/**
 * Key ONLY exterior studio matte (edge flood-fill). Global near-black key
 * punched pupils → eyes vanished after downsample/alpha gate.
 */
function knockOutMatte(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  const seen = new Uint8Array(w * h);
  const qx = new Int32Array(w * h);
  const qy = new Int32Array(w * h);
  let qh = 0;
  let qt = 0;

  const trySeed = (x: number, y: number) => {
    const p = y * w + x;
    if (seen[p]) return;
    const i = p * 4;
    if (!isStudioMatte(px[i]!, px[i + 1]!, px[i + 2]!, px[i + 3]!)) return;
    seen[p] = 1;
    qx[qt] = x;
    qy[qt] = y;
    qt++;
  };

  for (let x = 0; x < w; x++) {
    trySeed(x, 0);
    trySeed(x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    trySeed(0, y);
    trySeed(w - 1, y);
  }

  while (qh < qt) {
    const x = qx[qh]!;
    const y = qy[qh]!;
    qh++;
    const i = (y * w + x) * 4;
    px[i] = 0;
    px[i + 1] = 0;
    px[i + 2] = 0;
    px[i + 3] = 0;
    const neighbors = [
      [x - 1, y],
      [x + 1, y],
      [x, y - 1],
      [x, y + 1],
    ] as const;
    for (const [nx, ny] of neighbors) {
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const p = ny * w + nx;
      if (seen[p]) continue;
      const ni = p * 4;
      if (!isStudioMatte(px[ni]!, px[ni + 1]!, px[ni + 2]!, px[ni + 3]!)) continue;
      seen[p] = 1;
      qx[qt] = nx;
      qy[qt] = ny;
      qt++;
    }
  }

  eatDarkFringe(px, w, h, 48, 28, 56);
  eatDarkFringe(px, w, h, 56, 34, 64);
  ctx.putImageData(data, 0, 0);
}

function eatDarkFringe(
  px: Uint8ClampedArray,
  w: number,
  h: number,
  maxLuma: number,
  maxSat: number,
  maxChan: number,
): void {
  const alpha = new Uint8Array(w * h);
  for (let i = 0, p = 0; i < px.length; i += 4, p++) {
    alpha[p] = px[i + 3]!;
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      const i = p * 4;
      if (alpha[p]! === 0) continue;
      const r = px[i]!;
      const g = px[i + 1]!;
      const b = px[i + 2]!;
      const maxc = Math.max(r, g, b);
      const minc = Math.min(r, g, b);
      const yL = luma(r, g, b);
      const sat = maxc - minc;
      if (!(yL <= maxLuma && sat <= maxSat && maxc <= maxChan)) continue;
      let touchesHole = false;
      for (let dy = -1; dy <= 1 && !touchesHole; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) {
            touchesHole = true;
            break;
          }
          if (alpha[ny * w + nx]! < 8) {
            touchesHole = true;
            break;
          }
        }
      }
      if (touchesHole) px[i + 3] = 0;
    }
  }
}

function opaqueBounds(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): { x: number; y: number; w: number; h: number } {
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3]!;
      if (a < 16) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || maxY < minY) {
    return { x: 0, y: 0, w, h };
  }
  const pad = 1;
  const x = Math.max(0, minX - pad);
  const y = Math.max(0, minY - pad);
  const bw = Math.min(w - x, maxX - minX + 1 + pad * 2);
  const bh = Math.min(h - y, maxY - minY + 1 + pad * 2);
  return { x, y, w: bw, h: bh };
}

/**
 * Integer-only fit toward targetH. Native small sources (≤64px) stay 1:1
 * when already near the target; large legacy sources NN-scale by an integer
 * factor so height lands close to targetH without fractional resampling.
 */
function integerFitSize(
  srcW: number,
  srcH: number,
  targetH: number,
): { outW: number; outH: number } {
  if (srcH <= 0) return { outW: 1, outH: Math.max(1, targetH) };

  // Native / already-game-sized: prefer identity.
  if (srcH <= 64 && srcW <= 96) {
    if (Math.abs(srcH - targetH) <= 4) {
      return { outW: srcW, outH: srcH };
    }
    if (srcH > targetH) {
      const factor = Math.max(1, Math.floor(srcH / targetH));
      return {
        outW: Math.max(1, Math.floor(srcW / factor)),
        outH: Math.max(1, Math.floor(srcH / factor)),
      };
    }
    const factor = Math.max(1, Math.round(targetH / srcH));
    return { outW: srcW * factor, outH: srcH * factor };
  }

  // Legacy large stills — nearest integer downsample toward targetH.
  const factor = Math.max(1, Math.round(srcH / targetH));
  return {
    outW: Math.max(1, Math.round(srcW / factor)),
    outH: Math.max(1, Math.round(srcH / factor)),
  };
}

/**
 * Bake source art onto an integer pixel grid with NN only, then convert to
 * vector-pixel runs (hard edges, no soft fringe in the runtime draw path).
 */
function bakeVectorSprite(
  source: HTMLImageElement,
  targetH: number,
  flipX: boolean,
): Prepared {
  const full = document.createElement("canvas");
  full.width = source.naturalWidth || source.width;
  full.height = source.naturalHeight || source.height;
  const fctx = full.getContext("2d")!;
  fctx.imageSmoothingEnabled = false;
  fctx.drawImage(source, 0, 0);
  knockOutMatte(fctx, full.width, full.height);
  const box = opaqueBounds(fctx, full.width, full.height);

  const { outW, outH } = integerFitSize(box.w, box.h, targetH);
  const mid = document.createElement("canvas");
  mid.width = outW;
  mid.height = outH;
  const mctx = mid.getContext("2d")!;
  mctx.imageSmoothingEnabled = false;
  if (flipX) {
    mctx.translate(outW, 0);
    mctx.scale(-1, 1);
  }
  mctx.drawImage(full, box.x, box.y, box.w, box.h, 0, 0, outW, outH);
  knockOutMatte(mctx, outW, outH);

  // Hard alpha gate drops soft fringe before run extraction.
  const vector = canvasToVectorSprite(mid, 170);
  const canvas = vectorSpriteToCanvas(vector);
  return { vector, canvas, sm: canvas, lg: canvas };
}

async function prepareCreature(key: CreatureKey): Promise<Prepared> {
  const img = await loadImage(`${CREATURE_BASE}/${key}.png`);
  return bakeVectorSprite(img, GAME_SPRITE_H, CREATURE_SOURCE_FACES_LEFT[key]);
}

async function prepareDragon(key: DragonKey): Promise<Prepared> {
  const img = await loadImage(`${DRAGON_BASE}/${key}.png`);
  return bakeVectorSprite(img, GAME_DRAGON_H, false);
}

/** Kick off asset decode; safe to call multiple times. */
export function preloadTheaterAssets(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const keys = [...new Set(BODY_CREATURE_KEYS)];
    const results = await Promise.allSettled([
      ...keys.map(async (key) => {
        creatureCache.set(key, await prepareCreature(key));
      }),
      ...DRAGON_SPRITE_KEYS.map(async (key) => {
        dragonCache.set(key, await prepareDragon(key));
      }),
      loadShoreBackdrop().then((img) => {
        shoreImage = img;
      }),
    ]);
    for (const r of results) {
      if (r.status === "rejected") {
        console.warn("theater asset failed", r.reason);
      }
    }
  })();
  return loadPromise;
}

export function getCreatureVector(body: number): VectorSprite | null {
  const key = BODY_CREATURE_KEYS[body % BODY_CREATURE_KEYS.length]!;
  return creatureCache.get(key)?.vector ?? null;
}

export function getDragonVector(color: number): VectorSprite | null {
  const key = DRAGON_SPRITE_KEYS[color % DRAGON_SPRITE_KEYS.length]!;
  return dragonCache.get(key)?.vector ?? null;
}

/** Legacy canvas accessor — rebuilt from vector runs (hard pixels). */
export function getCreatureSprite(
  body: number,
  _large?: boolean,
): HTMLCanvasElement | null {
  const key = BODY_CREATURE_KEYS[body % BODY_CREATURE_KEYS.length]!;
  return creatureCache.get(key)?.canvas ?? null;
}

export function getDragonSprite(color: number): HTMLCanvasElement | null {
  const key = DRAGON_SPRITE_KEYS[color % DRAGON_SPRITE_KEYS.length]!;
  return dragonCache.get(key)?.canvas ?? null;
}

export function getShoreBackdrop(): HTMLImageElement | null {
  return shoreImage;
}

export function drawShoreBackdrop(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): boolean {
  const img = shoreImage;
  if (!img?.complete || !img.naturalWidth) return false;
  ctx.imageSmoothingEnabled = false;
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const scale = Math.max(w / iw, h / ih);
  const dw = Math.ceil(iw * scale);
  const dh = Math.ceil(ih * scale);
  const dx = Math.floor((w - dw) / 2);
  const dy = Math.floor((h - dh) * 0.35);
  ctx.drawImage(img, dx, dy, dw, dh);
  return true;
}
